if (!customElements.get("m-announcement-bar")) {
  class MAnnouncementBar extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const enableSlider = this.dataset.enableSlider === "true";
      if (!enableSlider) return;
      this.initSlider();
    }

    initSlider() {
      const autoplay = this.dataset.enableAutoplay === "true";
      const autoplaySpeed = this.dataset.autoplaySpeed;
      const slideContainer = this.querySelector(".swiper-container");
      if (slideContainer) {
        this.slider = new MinimogLibs.Swiper(slideContainer, {
          slidesPerView: 1,
          slidesPerGroup: 1,
          autoplay: autoplay
            ? {
                delay: parseInt(autoplaySpeed) * 1000,
              }
            : false,
          loop: true,
          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },
        });
      }
    }
  }

  customElements.define("m-announcement-bar", MAnnouncementBar);
}

if (!customElements.get("m-announcement-marquee")) {
  class MAnnouncementMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".m-announcement-marquee__track");
      this.group = this.track && this.track.firstElementChild;
      if (!this.group) return;

      // Respect the OS setting: no clones, no animation, nothing to pause.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      this.speed = parseFloat(this.dataset.speed) || 60;
      this.measure = this.measure.bind(this);
      this.measure();

      // Web fonts land after first paint and change the group width.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.measure);
      }

      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(this.measure, 150);
      });
      this.resizeObserver.observe(this);

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle("m-announcement-marquee--paused", !entry.isIntersecting);
          });
        },
        { rootMargin: "0px 0px 50px 0px" }
      );
      this.intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      clearTimeout(this.resizeTimer);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    measure() {
      if (!this.isConnected) return;

      // Measure first, mutate later. Clones are identical to the original and
      // sized independently, so they never skew this reading.
      const shift = Math.round(this.group.getBoundingClientRect().width);
      if (!shift) return;

      // Fall back to the viewport if the host has not been laid out yet —
      // a zero here would under-fill the track and leave a gap on wide screens.
      const hostWidth = this.getBoundingClientRect().width || window.innerWidth;

      // The track must stay covered after translating a full group to the left,
      // so it needs to span the visible width plus one extra group.
      const copies = Math.max(1, Math.ceil(hostWidth / shift) + 1);

      // Nothing changed: leave the running animation and its clones alone.
      if (this._shift === shift && this._copies === copies) return;
      this._shift = shift;
      this._copies = copies;

      this.track.querySelectorAll("[data-marquee-clone]").forEach((clone) => clone.remove());

      const fragment = document.createDocumentFragment();
      for (let i = 0; i < copies; i++) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("data-marquee-clone", "");
        fragment.appendChild(clone);
      }
      this.track.appendChild(fragment);

      // Restart from x=0 so the first message always enters from its start.
      this.classList.remove("m-announcement-marquee--animated");
      void this.track.offsetWidth;

      // duration = distance / speed keeps px-per-second constant.
      this.style.setProperty("--marquee-shift", shift + "px");
      this.style.setProperty("--marquee-duration", shift / this.speed + "s");
      this.classList.add("m-announcement-marquee--animated");
    }
  }

  customElements.define("m-announcement-marquee", MAnnouncementMarquee);
}

// Delegated so it survives theme-editor section reloads and only ever removes
// the bar that was actually clicked.
if (!window.MAnnouncementCloseBound) {
  window.MAnnouncementCloseBound = true;
  document.addEventListener("click", (event) => {
    const closeButton = event.target.closest(".m-announcement-bar__close");
    if (!closeButton) return;
    const bar = closeButton.closest(".m-announcement-bar");
    if (!bar) return;
    bar.remove();
    document.documentElement.style.setProperty("--m-announcement-height", "0px");
  });
}
